import { Box, Divider, Typography, Switch } from '@material-ui/core'
import currency from 'currency.js'
import React from 'react'
import styled from 'styled-components'

import PriceBreakdown, { DeliveryFeeContribution, PriceRow } from '../checkout/PriceBreakdown'
import { formatCurrencyInCents } from '../../util/formatUtils'
import CartMenuItemList from './CartMenuItemList'

const CartContainer = styled.div`
  padding-top: 0px;
`

const YourOrder = styled.div`
  font-size: 20px;
  padding-bottom: 10px;
  font-weight: 500;
  line-height: 23px;
  padding-top: 10px;
`

const thumbOffset = 5

export const CustomSwitch = styled(Switch)(() => ({
  '&.MuiSwitch-root': { width: '58px', paddingLeft: 0, paddingRight: 0, marginRight: 5 },
  '& .MuiSwitch-switchBase': {
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '& .Mui-focusVisible .MuiSwitch-thumb': {
      boxShadow: 'none',
    },
    '& .Mui-ripple-visible': {
      display: 'none',
    },
    transform: `translateX(${thumbOffset}px)`,
    '&.Mui-checked': {
      color: '#fff',
      '& .MuiSwitch-thumb': {
        transform: `translateX(calc(50% - ${thumbOffset * 2}px))`,
        backgroundColor: '#AB2B19',
        height: '26px',
        width: '26px',
        '&::before': {
          content: '"SPLIT"',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '9px',
          marginTop: '8px',
        },
      },
      '& + .MuiSwitch-track': {
        backgroundColor: '#e5aca4',
        opacity: 1,
      },
    },
    '&:not(.Mui-checked)': {
      color: '#fff',
      '& .MuiSwitch-thumb': {
        transform: `translateX(calc(-15% - ${thumbOffset * 2}px))`,
        backgroundColor: 'grey',
        height: '26px',
        width: '26px',
        '&::before': {
          content: '"PAY"',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '9px',
          color: 'white',
          marginTop: '8px',
        },
      },
      '& + .MuiSwitch-track': {
        backgroundColor: '#D5D6D8',
        opacity: 1,
        width: `calc(100% - ${thumbOffset * 2}px)`,
      },
    },
  },
}))

export const OrderCart = ({
  cart: { itemSubTotal, orderItems },
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  orderMinimumInCents,
  showQuantity,
  deliveryFee,
  toggleStatus,
  toggleStatusSet,
  isConfirmedReserveDropoff,
}) => {
  const deliveryFeeInDollars = isConfirmedReserveDropoff ? deliveryFee : deliveryFee / 100

  function setUndefinedItemSubtotalToZero(value) {
    return Number.isNaN(value) || value === undefined ? 0 : value
  }

  return (
    <CartContainer>
      <YourOrder>Your Order</YourOrder>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />
      <Box marginBottom={2} marginTop={3}>
        <Divider />
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" fontWeight="bold">
          Subtotal
        </Typography>

        <Typography fontSize={30} fontWeight={700}>
          {currency(setUndefinedItemSubtotalToZero(itemSubTotal), {
            formatWithSymbol: true,
          }).format()}
        </Typography>
      </Box>
      {deliveryFee > 0 && (
        <>
          <DeliveryFeeContribution
            deliveryFeeContribution={deliveryFeeInDollars}
            toggleStatus={toggleStatus}
            toggleStatusSet={toggleStatusSet}
            isReserveConfirmedDropoff={isConfirmedReserveDropoff}
            isOrderSummary={false}
          />
          <Box marginBottom={2} marginTop={3}>
            <Divider />
          </Box>
          <PriceRow
            primary
            label="Total"
            value={currency(deliveryFeeInDollars + setUndefinedItemSubtotalToZero(itemSubTotal), {
              formatWithSymbol: true,
            }).format()}
          />
        </>
      )}
      {orderMinimumInCents && (
        <Box display="flex" justifyContent="right">
          <Typography variant="body2" color="textSecondary">
            {`${formatCurrencyInCents(orderMinimumInCents)} minimum`}
          </Typography>
        </Box>
      )}
    </CartContainer>
  )
}

export const CheckoutCart = ({
  cart: {
    foodsbyFee,
    orderItems,
    orderTotal,
    taxSubTotal,
    taxSubtotal,
    itemSubTotal,
    deliveryFeeContribution,
  }, //again, need to support but subTotal spellings :(
  couponUsage,
  creditUsage,
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  showQuantity,
  programUsage = {},
  storedCurrencyUsage = {},
  isCatering,
}) => {
  return (
    <CartContainer>
      <Typography gutterBottom variant="overline">
        Your order
      </Typography>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />
      <PriceBreakdown
        couponAmount={couponUsage.couponAmount}
        couponApplied={!couponUsage.isEpp && couponUsage.couponApplied}
        couponCode={couponUsage.couponCode}
        creditsApplied={creditUsage.creditsApplied}
        creditsUsed={creditUsage.creditBalanceUsed}
        orderFee={foodsbyFee}
        subtotal={itemSubTotal}
        programAmount={programUsage?.programAmount || couponUsage.couponAmount}
        programApplied={
          programUsage?.programApplied || (couponUsage.couponApplied && couponUsage.isEpp)
        }
        programCode={programUsage?.programName || couponUsage.couponCode}
        storedCurrencyAmount={storedCurrencyUsage?.storedCurrencyBalanceUsed}
        storedCurrencyApplied={storedCurrencyUsage?.storedCurrencyApplied}
        taxes={taxSubTotal || taxSubtotal}
        orderTotal={orderTotal}
        deliveryFeeContribution={deliveryFeeContribution}
        isCatering={isCatering}
      />
    </CartContainer>
  )
}

export const ReserveCheckoutCart = ({
  cart: {
    foodsbyFee,
    orderItems,
    orderTotal,
    preTaxSubTotal,
    taxSubTotal,
    taxSubtotal,
    deliveryFeeContribution,
    itemSubTotal,
  }, //again, need to support but subTotal spellings :(
  couponUsage,
  creditUsage,
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  showQuantity,
  programUsage = {},
  storedCurrencyUsage = {},
  isSplit,
  toggleStatus,
  toggleStatusSet,
  isReserveConfirmedDropoff,
}) => {
  return (
    <CartContainer>
      <Typography gutterBottom variant="overline">
        Your order
      </Typography>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />
      <PriceBreakdown
        couponAmount={couponUsage.couponAmount}
        couponApplied={!couponUsage.isEpp && couponUsage.couponApplied}
        couponCode={couponUsage.couponCode}
        creditsApplied={creditUsage.creditsApplied}
        creditsUsed={creditUsage.creditBalanceUsed}
        orderFee={foodsbyFee}
        subtotal={itemSubTotal}
        programAmount={programUsage?.programAmount || couponUsage.couponAmount}
        programApplied={
          programUsage?.programApplied || (couponUsage.couponApplied && couponUsage.isEpp)
        }
        programCode={programUsage?.programName || couponUsage.couponCode}
        storedCurrencyAmount={storedCurrencyUsage?.storedCurrencyBalanceUsed}
        storedCurrencyApplied={storedCurrencyUsage?.storedCurrencyApplied}
        taxes={taxSubTotal || taxSubtotal}
        orderTotal={orderTotal}
        deliveryFeeContribution={deliveryFeeContribution}
        isSplit={isSplit}
        toggleStatus={toggleStatus}
        toggleStatusSet={toggleStatusSet}
        isReserveConfirmedDropoff={isReserveConfirmedDropoff}
      />
    </CartContainer>
  )
}
